import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import PaginatedTable from '../../components/PaginatedTable';
import TitleWithBack from '../../components/common/TitleWithBack';

function ExamTypesView() {
  const [examTypes, setExamTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [tooltip, setTooltip] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchExamTypes();
  }, []);

  const fetchExamTypes = () => {
    setLoading(true);
    axios.get('/api/exam-types')
      .then(response => {
        const data = Array.isArray(response.data) ? response.data : [];
        data.sort((a, b) => b.id - a.id);
        setExamTypes(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Error fetching exam types.');
        setLoading(false);
      });
  };

  const handleDelete = (id, canDelete) => {
    setConfirmDeleteId(id);
  };

  const confirmDeletion = () => {
    axios.delete(`/api/exam-types/${confirmDeleteId}`)
      .then(() => {
        setTooltip('Exam type deleted successfully!');
        fetchExamTypes();
        setConfirmDeleteId(null);
      })
      .catch(() => {
        setTooltip('Error deleting exam type.');
        setConfirmDeleteId(null);
      });
  };

  const cancelDeletion = () => {
    setConfirmDeleteId(null);
  };

  const handleEdit = (id) => {
    navigate(`/admin/exam-types/edit/${id}`);
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  const sortedExamTypes = [...examTypes].sort((a, b) => {
    const valA = a[sortKey] || '';
    const valB = b[sortKey] || '';
    if (typeof valA === 'number' && typeof valB === 'number') {
      return sortOrder === 'asc' ? valA - valB : valB - valA;
    }
    return sortOrder === 'asc'
      ? valA.toString().localeCompare(valB.toString())
      : valB.toString().localeCompare(valA.toString());
  });

  const filteredExamTypes = sortedExamTypes.filter(type =>
    type.name.toLowerCase().includes(filter.toLowerCase())
  );

  const columns = [
    { key: 'serial', header: <>#</> },
    { key: 'name', header: <>Exam Type</> },
    { key: 'requiredDocuments', header: 'Required Documents' },
    { key: 'actions', header: 'Actions' }
  ];

  const renderRow = (type, idx, pageIndex, pageSize) => (
    <>
      <td>{(pageIndex * pageSize) + idx + 1}</td>
      <td><strong>{type.name}</strong></td>
      <td>{type.requiredDocuments.join(', ')}</td>
      <td>
        <button className="btn btn-sm btn-primary me-2" onClick={() => handleEdit(type.id)}>
          Edit
        </button>
        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(type.id, type.canDelete)}>
          Delete
        </button>
      </td>
    </>
  );

  return (
    <div className="container mt-2">
      <TitleWithBack title="View Exam Types" />
      <div className="mb-3 d-flex align-items-center">
        <input 
          type="text"
          className="form-control me-2"
          style={{ maxWidth: '300px' }}
          placeholder="Search exam type..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <button className="btn btn-secondary" onClick={() => handleSort('name')}>
          Sort by Name {sortKey === 'name' && (sortOrder === 'asc' ? '▲' : '▼')}
        </button>
      </div>
      {loading ? (
        <p>Loading exam types...</p>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : filteredExamTypes.length === 0 ? (
        <p>Nothing to show here</p>
      ) : (
        <PaginatedTable
          data={filteredExamTypes}
          columns={columns}
          renderRow={renderRow}
          rowsPerPage={50}
        />
      )}
      {tooltip && (
        <div className={`mt-3 alert ${tooltip.startsWith('Error') ? 'alert-danger' : 'alert-success'}`}>
          {tooltip}
        </div>
      )}
      {confirmDeleteId && (
        <ConfirmationModal 
          title="Confirm Delete"
          message="Are you sure you want to delete this exam type?"
          onConfirm={confirmDeletion}
          onCancel={cancelDeletion}
        />
      )}
    </div>
  );
}

export default ExamTypesView;
